import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { fmtDuration } from "../lib/duration";
import { Package, Anchor, CheckCircle, ClockCounterClockwise, X } from "@phosphor-icons/react";

/**
 * Log Hatch Tons — quick per-hatch tons entry modal.
 * Supervisor picks a hatch, enters tons, submits.  Backend appends a report
 * for that hatch and recomputes the vessel's TOB as the sum of all hatch
 * report tons. TOB stays independently editable via the existing TOB modal.
 */
export default function LogHatchTonsModal({ vessel, onClose, onLogged }) {
    const [summary, setSummary] = useState(null);
    const [hatchNo, setHatchNo] = useState(vessel?.current_hatch || 1);
    const [tons, setTons] = useState("");
    const [notes, setNotes] = useState("");
    const [reason, setReason] = useState("");
    // "log" = additive (default), "correct" = set exact total, up or down, with a mandatory reason.
    // Corrections are for fixing a mis-recorded value; they preserve the audit trail by inserting a
    // signed reconciling report through /hatches/{n}/correct-tons.
    const [mode, setMode] = useState("log");
    const [saving, setSaving] = useState(false);
    // Synchronous re-entry lock — React state updates aren't instant, so a
    // second tap can slip through before `saving=true` renders and disables
    // the button. This ref blocks the second call at the very first line of
    // the handler regardless of render timing.
    const inFlightRef = useRef(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/vessels/${vessel.id}/hatches`);
                setSummary(data);
                if (data?.current_hatch) setHatchNo(data.current_hatch);
            } catch { /* ignore */ }
        })();
    }, [vessel.id]);

    const hatches = summary?.hatches || [];
    const N = summary?.total_hatches || hatches.length || 8;
    const currentHatchTons = useMemo(() => {
        const h = hatches.find((x) => x.hatch_no === hatchNo);
        return Math.round(Number(h?.tons_loaded || 0));
    }, [hatches, hatchNo]);
    const running = useMemo(() => {
        const sum = hatches.reduce((acc, h) => acc + Number(h.tons_loaded || 0), 0);
        return Math.round(sum);
    }, [hatches]);
    // User enters the NEW ABSOLUTE total for the hatch. We derive the delta
    // (what actually gets appended to the reports collection).
    //   log mode:      only positive deltas accepted (additive)
    //   correct mode:  any non-zero delta accepted (up OR down)
    const parsedTotal = Number(tons) || 0;
    const delta = parsedTotal - currentHatchTons;
    const isCorrection = mode === "correct";
    const validDelta = isCorrection ? (delta !== 0 && tons !== "") : delta > 0;
    const reasonValid = !isCorrection || reason.trim().length >= 3;
    const projectedTob = running + delta;

    const submit = async () => {
        if (inFlightRef.current) return;          // hard block — second tap
        if (!hatchNo || hatchNo < 1) return toast.error("Pick a hatch");
        if (parsedTotal < 0) return toast.error("Total must be 0 or greater");
        if (delta === 0) return toast.error(`H${hatchNo} is already at ${currentHatchTons.toLocaleString()} t — nothing to change`);
        if (!isCorrection && delta < 0) return toast.error(`New total (${parsedTotal.toLocaleString()} t) must be greater than the previous H${hatchNo} total (${currentHatchTons.toLocaleString()} t). Switch to Correct mode to reduce it.`);
        if (isCorrection && !reasonValid) return toast.error("Please provide a reason for the correction (min 3 chars)");
        inFlightRef.current = true;
        try {
            setSaving(true);
            const idem = `${vessel.id}:${hatchNo}:${mode}:${parsedTotal}:${Date.now()}:${Math.random().toString(36).slice(2, 10)}`;
            let data;
            if (isCorrection) {
                ({ data } = await api.post(
                    `/vessels/${vessel.id}/hatches/${hatchNo}/correct-tons`,
                    { new_total: parsedTotal, reason: reason.trim() },
                    { headers: { "X-Idempotency-Key": idem } },
                ));
                toast.success(`H${hatchNo} corrected to ${parsedTotal.toLocaleString()} t (${delta > 0 ? "+" : ""}${delta.toLocaleString()}) · TOB ${Math.round(data.auto_tob).toLocaleString()} t`);
            } else {
                ({ data } = await api.post(
                    `/vessels/${vessel.id}/hatches/${hatchNo}/log-tons`,
                    { tons_loaded: delta, notes: notes.trim() || undefined },
                    { headers: { "X-Idempotency-Key": idem } },
                ));
                toast.success(`H${hatchNo} now ${parsedTotal.toLocaleString()} t (+${delta.toLocaleString()}) · TOB ${Math.round(data.auto_tob).toLocaleString()} t`);
            }
            onLogged?.(data);
            onClose?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to save hatch tons");
        } finally {
            setSaving(false);
            setTimeout(() => { inFlightRef.current = false; }, 100);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-2 sm:p-4" data-testid="log-hatch-tons-modal">
            <div className="bg-white w-full max-w-md rounded-sm shadow-2xl border border-slate-200 flex flex-col max-h-[92vh]">
                <div className="p-4 border-b border-slate-200 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-[var(--grindrod-navy)] text-white flex items-center justify-center">
                        <Anchor size={18} weight="fill" />
                    </div>
                    <div className="flex-1">
                        <div className="font-display font-black uppercase tracking-wide">{isCorrection ? "Correct hatch total" : "Log hatch tons"}</div>
                        <div className="text-xs text-slate-500">{isCorrection ? "Set the exact tons on the hatch — up or down — with a reason." : "Enter the new running total for the currently-loading hatch. It replaces the previous value."}</div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="log-hatch-close" aria-label="Close">
                        <X size={20} />
                    </button>
                </div>

                {/* Mode toggle — segmented control */}
                <div className="px-4 pt-3">
                    <div className="inline-flex bg-slate-100 rounded-md p-0.5 w-full sm:w-auto" data-testid="log-hatch-mode-toggle">
                        <button
                            type="button"
                            onClick={() => { setMode("log"); setReason(""); }}
                            className={`flex-1 sm:flex-none px-3 py-1.5 rounded text-xs font-bold uppercase tracking-wide transition ${
                                mode === "log" ? "bg-white text-[var(--grindrod-navy)] shadow-sm" : "text-slate-500 hover:text-slate-700"
                            }`}
                            data-testid="log-hatch-mode-log"
                        >
                            Log tons
                        </button>
                        <button
                            type="button"
                            onClick={() => setMode("correct")}
                            className={`flex-1 sm:flex-none px-3 py-1.5 rounded text-xs font-bold uppercase tracking-wide transition ${
                                mode === "correct" ? "bg-white text-[#B45309] shadow-sm" : "text-slate-500 hover:text-slate-700"
                            }`}
                            data-testid="log-hatch-mode-correct"
                        >
                            Correct total
                        </button>
                    </div>
                </div>

                <div className="p-4 space-y-4 overflow-y-auto">
                    {/* Hatch selector */}
                    <div>
                        <label className="uppercase-label block mb-1.5">Currently-loading hatch *</label>
                        <div className="grid grid-cols-4 sm:grid-cols-8 gap-1.5" data-testid="log-hatch-picker">
                            {Array.from({ length: N }, (_, i) => i + 1).map((n) => {
                                const info = hatches.find((h) => h.hatch_no === n);
                                const isSelected = hatchNo === n;
                                const isCurrent = summary?.current_hatch === n;
                                return (
                                    <button
                                        key={n}
                                        type="button"
                                        onClick={() => setHatchNo(n)}
                                        className={`min-w-0 px-1 py-1.5 rounded-md border text-xs font-bold flex flex-col items-center justify-center gap-0.5 transition ${
                                            isSelected
                                                ? "bg-[#2563EB] border-[#2563EB] text-white shadow-md"
                                                : "bg-white border-slate-300 text-slate-700 hover:border-[#2563EB] hover:text-[#2563EB]"
                                        }`}
                                        data-testid={`log-hatch-pick-${n}`}
                                    >
                                        <span className="font-display font-black text-[13px] leading-none">H{n}</span>
                                        <span className="text-[9px] tabular opacity-90 truncate max-w-full">
                                            {Math.round(Number(info?.tons_loaded || 0)).toLocaleString()}<span className="opacity-70"> t</span>
                                        </span>
                                        {isCurrent && !isSelected && <span className="text-[8px] font-bold text-[#2563EB] uppercase leading-none">Active</span>}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Tons input */}
                    <div>
                        <label className="block mb-1.5">
                            <span className="uppercase-label block">{isCorrection ? `Corrected total on H${hatchNo}` : `New total on H${hatchNo}`}</span>
                            <span className="text-[11px] text-slate-500 font-normal block mt-0.5">
                                {isCorrection ? "Any value" : "Replaces previous"} · currently <span className="tabular font-bold text-slate-700">{currentHatchTons.toLocaleString()} t</span>
                            </span>
                        </label>
                        <div className="relative">
                            <Package size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                type="number"
                                min="0"
                                step="10"
                                placeholder={`e.g. ${(isCorrection ? Math.max(0, currentHatchTons - 500) : currentHatchTons + 500).toLocaleString()}`}
                                className="g-input tabular pl-11 pr-14 text-lg font-bold"
                                value={tons}
                                onChange={(e) => setTons(e.target.value)}
                                data-testid="log-hatch-tons-input"
                                autoFocus
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 font-semibold text-sm">tons</span>
                        </div>
                        {tons && delta < 0 && !isCorrection && (
                            <div className="text-[11px] text-red-600 mt-1 font-semibold" data-testid="log-hatch-delta-warning">
                                New total must be greater than the previous H{hatchNo} value ({currentHatchTons.toLocaleString()} t). Switch to <b>Correct total</b> to reduce it.
                            </div>
                        )}
                        {tons && delta > 0 && !isCorrection && (
                            <div className="text-[11px] text-slate-500 mt-1 tabular" data-testid="log-hatch-delta-hint">
                                +<span className="font-bold text-[#2563EB]">{delta.toLocaleString()} t</span> added to H{hatchNo} · running total will be <span className="font-bold text-slate-700">{parsedTotal.toLocaleString()} t</span>
                            </div>
                        )}
                        {tons && isCorrection && delta !== 0 && (
                            <div className="text-[11px] mt-1 tabular" data-testid="log-hatch-correction-hint">
                                <span className={`font-bold ${delta > 0 ? "text-[#10B981]" : "text-[#DC2626]"}`}>{delta > 0 ? "+" : ""}{delta.toLocaleString()} t</span>
                                <span className="text-slate-500"> correction · H{hatchNo} will become </span>
                                <span className="font-bold text-slate-700">{parsedTotal.toLocaleString()} t</span>
                            </div>
                        )}
                    </div>

                    {/* Reason (correction) OR Notes (log) */}
                    {isCorrection ? (
                        <div>
                            <label className="uppercase-label block mb-1.5">
                                Reason for correction <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                className="g-input"
                                placeholder="e.g. Operator typo — meant 2,000 not 4,000. Weigh-bridge re-check confirmed."
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                data-testid="log-hatch-reason"
                                maxLength={280}
                                rows={2}
                            />
                            <div className="text-[10px] text-slate-400 mt-1">
                                Correction is permanent — the audit trail keeps the original entry AND the correction with your name & timestamp.
                            </div>
                        </div>
                    ) : (
                        <div>
                            <label className="uppercase-label block mb-1.5">Notes <span className="text-slate-400 font-normal">(optional)</span></label>
                            <input
                                className="g-input"
                                placeholder="Product, shift, weigh-bridge slip #, …"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                data-testid="log-hatch-notes"
                                maxLength={280}
                            />
                        </div>
                    )}

                    {/* Live TOB projection */}
                    <div className={`rounded-sm border p-3 flex items-start gap-3 ${isCorrection ? "border-[#FED7AA] bg-[#FFFBEB]" : "border-[#DBEAFE] bg-[#EFF6FF]"}`} data-testid="log-hatch-projection">
                        <div className={`w-8 h-8 rounded-full text-white flex items-center justify-center flex-shrink-0 ${isCorrection ? "bg-[#B45309]" : "bg-[#2563EB]"}`}><CheckCircle size={16} weight="fill" /></div>
                        <div className="flex-1">
                            <div className={`text-[11px] uppercase-label font-bold ${isCorrection ? "text-[#B45309]" : "text-[#2563EB]"}`}>Automatic TOB (sum of hatches)</div>
                            <div className="text-slate-500 text-xs mt-0.5 tabular">
                                Current: <span className="font-semibold text-slate-800">{running.toLocaleString()} t</span>
                                <span className="mx-1.5 text-slate-400">→</span>
                                After entry: <span className={`font-display font-black text-lg ${isCorrection ? "text-[#B45309]" : "text-[#2563EB]"}`} data-testid="log-hatch-projected-tob">{projectedTob.toLocaleString()} t</span>
                            </div>
                            <div className="text-[10px] text-slate-400 mt-1">TOB stays independently editable via <b>Update TOB</b>.</div>
                        </div>
                    </div>

                    {/* Per-hatch running mini-list */}
                    {hatches.length > 0 && (
                        <div className="border border-slate-200 rounded-sm">
                            <div className="px-3 py-1.5 bg-slate-50 uppercase-label text-slate-500 text-[10px] flex items-center gap-2">
                                <ClockCounterClockwise size={11} /> Running tally
                            </div>
                            <div className="divide-y divide-slate-100">
                                {hatches.map((h) => (
                                    <div key={h.hatch_no} className="flex items-center justify-between px-3 py-1.5 text-[13px]">
                                        <span className="font-semibold">H{h.hatch_no}</span>
                                        <span className="tabular text-slate-700">{Math.round(h.tons_loaded).toLocaleString()} t</span>
                                        <span className="text-[10px] uppercase text-slate-400 tabular">{h.duration_min > 0 ? fmtDuration(h.duration_min) : "—"}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="p-3 border-t border-slate-200 flex justify-end gap-2">
                    <button onClick={onClose} className="btn-outline" data-testid="log-hatch-cancel">Cancel</button>
                    <button
                        onClick={submit}
                        disabled={saving || !validDelta || (isCorrection && !reasonValid)}
                        className={isCorrection ? "btn-primary !bg-[#B45309] !border-[#B45309] hover:!bg-[#92400E]" : "btn-primary"}
                        data-testid="log-hatch-submit"
                    >
                        {saving
                            ? (isCorrection ? "Saving…" : "Logging…")
                            : isCorrection
                                ? (validDelta ? `Correct H${hatchNo} to ${parsedTotal.toLocaleString()} t (${delta > 0 ? "+" : ""}${delta.toLocaleString()})` : `Correct H${hatchNo} total`)
                                : (validDelta ? `Set H${hatchNo} to ${parsedTotal.toLocaleString()} t (+${delta.toLocaleString()})` : `Set H${hatchNo} total`)
                        }
                    </button>
                </div>
            </div>
        </div>
    );
}
